import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import mixpanel from 'mixpanel-browser'
import { updateUserInfo } from '@/store/features/userSlice'
import cookies from '@/utils/cookies'

interface UserInfo {
  name: string
  email: string
  profile_image?: string
  user_id: string
  last_login: string
  role: string
  token: string
  is_onboarded: boolean
}

interface UseMixpanelUserOptions {
  trackLocation?: boolean
  useIPLocation?: boolean
}

export const useDispatchData = (options: UseMixpanelUserOptions = {}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isInitialized, setIsInitialized] = useState(false)

  const dispatchData = (userInfo: UserInfo) => {
    cookies.set('ai_tk', userInfo.token)
    dispatch(
      updateUserInfo({
        name: userInfo.name,
        email: userInfo.email,
        picture: userInfo.profile_image,
        userId: userInfo.user_id,
        lastLogin: userInfo.last_login,
        role: userInfo.role,
        isOnboarded: userInfo.is_onboarded,
      })
    )

    mixpanel.identify(userInfo.user_id)
    mixpanel.people.set({
      $name: userInfo.name,
      $email: userInfo.email,
      $last_login: userInfo.last_login,
    })

    if (options.trackLocation) {
      trackLocation()
    }

    setIsInitialized(true)

    if (userInfo?.is_onboarded) {
      navigate('/')
    } else {
      window.open(
        'https://play.aicade.io/onboarding?redirectTo=https://beta.aicade.io',
        '_self'
      )
    }
  }

  const trackLocation = async () => {
    console.log('Tracking Location')
    try {
      if (options.useIPLocation) {
        const locationData = await getIPBasedLocation()
        console.log('Location Data:', locationData)
        trackIPBasedLocation(locationData)
      }
    } catch (err) {
      console.error('Error tracking location:', err)
    }
  }

  const getIPBasedLocation = async () => {
    const response = await fetch('https://ipapi.co/json/')
    if (!response.ok) {
      throw new Error('Failed to fetch IP-based location')
    }
    return response.json()
  }

  const trackIPBasedLocation = (locationData: any) => {
    mixpanel.people.set({
      $city: locationData.city,
      $region: locationData.region,
      $country_code: locationData.country_code,
      ip_latitude: locationData.latitude,
      ip_longitude: locationData.longitude,
    })
    mixpanel.track('IP Location Updated', {
      city: locationData.city,
      region: locationData.region,
      country_code: locationData.country_code,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
    })
  }

  return { isInitialized, dispatchData }
}

export default useDispatchData
